import { SyncState, VisitDatetimeField, VisitOutcome } from '@axle-health/graphql-schema';

import { COFK_CONTAINER_KEYS } from '../containerKeys';
import { QueueDefinition, RootEntity } from '../types';

/**
 * Care Options for Kids is a pediatric therapy organization (OT / PT / SLP). Its
 * queues follow Pinnacle's, minus episodes, which the org does not have enabled.
 *
 * Every queue is declared in full here, including the ones whose filters happen to
 * match another org's. A queue is per-org configuration, so changing what this org
 * counts means editing this file and no other.
 */

// COFK eval visit service external IDs, the equivalent of Pinnacle's eval anchor.
const EVAL_SERVICE_IDS = [
    'AQgs1igzRC', // COFK OT Feeding Eval
    'VHZXmZF31w', // COFK PT Eval
    'vIumzDoNU0', // COFK SLP Eval
    '2uLY5XM6fr', // COFK SLP Feeding Eval
    'tCSbyJwvFC', // COFK OT Eval
    'ySGjr3EWPH', // COFK HH OT Eval
    'Uk77LWZg9D', // COFK HH PT Eval
    'uEVM2YK1rn', // COFK HH SLP Eval
    '6XfcgxZben', // COFK OASIS OT Eval
    'AeEDs1igzR', // COFK OASIS PT Eval
    'lN7niGAYKI', // COFK OASIS SLP Eval
];

// COFK discipline-discharge visit service external IDs, per discipline.
const DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    'seru8jaGon', // COFK HH OT DC Summary (Non-Billable)
    'Gp1oUcnUYI', // COFK HH PT DC Summary (Non-Billable)
    'eHIY6l8wVq', // COFK HH SLP DC Summary (Non-Billable)
];

// A completed eval in the same discipline re-admits the patient. The org has no
// ROC equivalent, so the eval codes stand alone here.
const DISCIPLINE_READMISSION_SERVICE_IDS = [...EVAL_SERVICE_IDS];

const POST_DISCHARGE_EXCLUDED_SERVICE_IDS: string[] = [];

// Eval visits that still need a clinician or a date.
const EVAL: QueueDefinition<RootEntity.VISIT> = {
    key: COFK_CONTAINER_KEYS.eval,
    label: 'Eval',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
        needsAssignment: { exact: true },
    }),
};

// Active patients carrying at least one visit that still needs assignment.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.PATIENT> = {
    key: COFK_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        isActive: { exact: true },
        hasVisits: {
            needsAssignment: { exact: true },
        },
    }),
};

// Pending visits left behind after a discipline discharge that a later
// same-discipline eval has not re-admitted.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: COFK_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        NOT: {
            services: {
                externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
            },
        },
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            sameDiscipline: true,
            filter: {
                services: {
                    externalIds: { value: { inList: DISCIPLINE_DISCHARGE_SERVICE_IDS } },
                },
                outcome: { exact: VisitOutcome.COMPLETED },
                NOT: {
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        sameDiscipline: true,
                        filter: {
                            services: {
                                externalIds: {
                                    value: { inList: DISCIPLINE_READMISSION_SERVICE_IDS },
                                },
                            },
                            outcome: { exact: VisitOutcome.COMPLETED },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gtOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    ltOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Pending visits that failed to reach the EMR.
const VISIT_SYNC_ISSUES: QueueDefinition<RootEntity.VISIT> = {
    key: COFK_CONTAINER_KEYS.visitSyncIssues,
    label: 'Visit Sync Issues',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: () => ({
        syncStatus: { state: { exact: SyncState.FAILING } },
        outcome: { exact: VisitOutcome.NONE },
    }),
};

export const CARE_OPTIONS_FOR_KIDS_QUEUES: QueueDefinition[] = [
    EVAL,
    PATIENTS_NEEDING_SCHEDULING,
    DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS,
    VISIT_SYNC_ISSUES,
];
